/**
 * Google Analytics 4 Adapter (Story 4.6)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls and delegates discovery to
 * the existing discoverGA4Properties() function.
 *
 * ADR-013: Adapter Pattern for API Isolation
 * ADR-4.6-A: Use batchRunReports (max 5 reports per request)
 * ADR-4.6-D / IMC-11: Three-Batch Reports Structure (5+5+3: 6 Overview chunks + 4 Breakdowns + 3 Demographics)
 * ADR-4.6-E: Date Range Defaults to "Yesterday"
 *
 * OAuth is NOT part of this adapter — handled by Story 4.5.
 * Token refresh is NOT implemented — deferred to Story 4.12+.
 * KV caching is NOT implemented — deferred to Story 4.11.
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { API_VERSIONS } from "../config";
import { discoverGA4Properties } from "./discovery";

// ============================================================================
// GA4 API Response Types (local to adapter, NOT exported)
// ============================================================================

interface GA4BatchResponse {
  reports?: GA4Report[];
  kind?: string;
}

interface GA4Report {
  dimensionHeaders?: Array<{ name: string }>;
  metricHeaders?: Array<{ name: string; type: string }>;
  rows?: GA4Row[];
  rowCount?: number;
  metadata?: {
    currencyCode?: string;
    timeZone?: string;
    dataLossFromOtherRow?: boolean;
    samplingMetadatas?: Array<{
      samplesReadCount: string;
      samplingSpaceSize: string;
    }>;
  };
  propertyQuota?: GA4PropertyQuota;
}

interface GA4PropertyQuota {
  tokensPerDay?: { consumed: number; remaining: number };
  tokensPerHour?: { consumed: number; remaining: number };
  concurrentRequests?: { consumed: number; remaining: number };
  serverErrorsPerProjectPerHour?: { consumed: number; remaining: number };
}

interface GA4Row {
  dimensionValues?: Array<{ value: string }>;
  metricValues?: Array<{ value: string }>;
}

// ============================================================================
// Report Index Constants (ADR-4.6-D, Failure 8a prevention)
// ============================================================================

/**
 * 3-Batch Architecture (IMC-11):
 * - Batch 1 (5 reports): Overview metric chunks 0-4 (10 metrics each, ≤10 per GA4 API limit)
 * - Batch 2 (5 reports): Overview chunk 5 (overflow) + 4 breakdown reports
 * - Batch 3 (3 reports): Demographic reports (newVsReturning, demographics, cities)
 *
 * All overview reports (chunks 0-5) are merged into a single "overview" record.
 */

/** Batch 1: Reports 0-4 (overview metric chunks, 10 metrics each) */
const BATCH1_OVERVIEW_COUNT = 5;

/** Batch 2: Reports 0-4 (overview overflow + breakdowns) */
const REPORT_INDEX_BATCH2 = {
  OVERVIEW_OVERFLOW: 0,
  TOP_PAGES: 1,
  TRAFFIC_SOURCES: 2,
  DEVICES: 3,
  GEOGRAPHY: 4,
} as const;

/** Batch 3: Reports 0-2 (demographic data) */
const REPORT_INDEX_BATCH3 = {
  NEW_VS_RETURNING: 0,
  DEMOGRAPHICS: 1,
  CITIES: 2,
} as const;

// ============================================================================
// Report Type Discriminators
// ============================================================================

type ReportType =
  | "overview"
  | "topPages"
  | "trafficSources"
  | "devices"
  | "geography"
  | "newVsReturning"
  | "demographics"
  | "cities";

/** Batch 2 report type map (index 0 = overview overflow, 1-4 = breakdowns) */
const REPORT_TYPE_MAP_BATCH2: Record<number, ReportType> = {
  [REPORT_INDEX_BATCH2.OVERVIEW_OVERFLOW]: "overview",
  [REPORT_INDEX_BATCH2.TOP_PAGES]: "topPages",
  [REPORT_INDEX_BATCH2.TRAFFIC_SOURCES]: "trafficSources",
  [REPORT_INDEX_BATCH2.DEVICES]: "devices",
  [REPORT_INDEX_BATCH2.GEOGRAPHY]: "geography",
};

/** Batch 3 report type map (demographics) */
const REPORT_TYPE_MAP_BATCH3: Record<number, ReportType> = {
  [REPORT_INDEX_BATCH3.NEW_VS_RETURNING]: "newVsReturning",
  [REPORT_INDEX_BATCH3.DEMOGRAPHICS]: "demographics",
  [REPORT_INDEX_BATCH3.CITIES]: "cities",
};

// ============================================================================
// Metric Value Parsing (Failure 8b prevention)
// ============================================================================

/**
 * Safely parse GA4 string metric values to numbers.
 * Uses Number.isFinite() to reject NaN, Infinity, and -Infinity.
 * Never use inline parseInt/parseFloat with || 0 — the || 0 pattern fails for Infinity.
 */
function parseMetricValue(
  value: string | undefined | null,
  type: "int" | "float"
): number {
  if (value === undefined || value === null || value === "") return 0;
  const parsed = type === "int" ? parseInt(value, 10) : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Build a header-based positional index to prevent data corruption.
 * ALWAYS use this — NEVER hardcode positional indices for metric/dimension access.
 * Used for both metric headers and dimension headers.
 */
function buildHeaderIndex(
  headers: Array<{ name: string }> | undefined
): Record<string, number> {
  const index: Record<string, number> = {};
  if (!headers) return index;
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    if (header) index[header.name] = i;
  }
  return index;
}

// ============================================================================
// GA4 Data API Base URL
// ============================================================================

const GA4_DATA_API_BASE = "https://analyticsdata.googleapis.com";

// ============================================================================
// Overview Metrics (split into chunks of ≤10 for GA4 API limit)
// ============================================================================

/**
 * GA4 Data API enforces a maximum of 10 metrics per report request.
 * All overview metrics use: no dimensions, limit 1, same dateRange.
 * Multiple overview reports are merged into a single "overview" record
 * by transformBatchResponse().
 */
const OVERVIEW_METRICS_CHUNKS: Array<Array<{ name: string }>> = [
  // Chunk 0: Core traffic + user engagement (10)
  [
    { name: "sessions" },
    { name: "activeUsers" },
    { name: "newUsers" },
    { name: "screenPageViews" },
    { name: "bounceRate" },
    { name: "averageSessionDuration" },
    { name: "engagedSessions" },
    { name: "engagementRate" },
    { name: "conversions" },
    { name: "totalUsers" },
  ],
  // Chunk 1: User engagement continued (10)
  [
    { name: "returningUsers" },
    { name: "sessionsPerUser" },
    { name: "userEngagementDuration" },
    { name: "1dayActiveUsers" },
    { name: "7dayActiveUsers" },
    { name: "28dayActiveUsers" },
    { name: "30dayActiveUsers" },
    { name: "dauPerMau" },
    { name: "dauPerWau" },
    { name: "wauPerMau" },
  ],
  // Chunk 2: E-commerce revenue (10)
  [
    { name: "purchaseRevenue" },
    { name: "totalRevenue" },
    { name: "grossPurchaseRevenue" },
    { name: "refundAmount" },
    { name: "revenuePerUser" },
    { name: "totalAdRevenue" },
    { name: "shippingValue" },
    { name: "purchases" },
    { name: "itemsPurchased" },
    { name: "itemsViewed" },
  ],
  // Chunk 3: E-commerce funnel + events (10)
  [
    { name: "addToCarts" },
    { name: "checkouts" },
    { name: "firstTimePurchasers" },
    { name: "purchaserConversionRate" },
    { name: "purchaseToViewRate" },
    { name: "totalPurchasers" },
    { name: "totalRefunds" },
    { name: "cartViewers" },
    { name: "firstTimePurchasersConversionRate" },
    { name: "eventCount" },
  ],
  // Chunk 4: Events continued + Google Ads linked (10)
  [
    { name: "eventCountPerUser" },
    { name: "eventsPerSession" },
    { name: "eventValue" },
    { name: "keyEvents" },
    { name: "userConversionRate" },
    { name: "sessionKeyEventRate" },
    { name: "advertiserAdClicks" },
    { name: "advertiserAdImpressions" },
    { name: "advertiserAdCost" },
    { name: "advertiserAdCostPerClick" },
  ],
  // Chunk 5: Ads overflow + Search Console + scroll (6)
  [
    { name: "advertiserAdCostPerConversion" },
    { name: "organicGoogleSearchClicks" },
    { name: "organicGoogleSearchImpressions" },
    { name: "organicGoogleSearchClickThroughRate" },
    { name: "organicGoogleSearchAveragePosition" },
    { name: "scrolledUsers" },
  ],
];

// ============================================================================
// Batch Report Request Builder
// ============================================================================

/**
 * Build batch 1: 5 overview metric reports (chunks 0-4, 10 metrics each).
 * All overview reports use no dimensions + limit 1, producing one row each.
 * ADR-4.6-D: GA4 batchRunReports allows max 5 reports per request.
 */
function buildBatch1Request(
  startDate: string,
  endDate: string
): AdapterResult<Record<string, unknown>> {
  const reports = OVERVIEW_METRICS_CHUNKS.slice(0, 5).map((metrics, i) => ({
    dateRanges: [{ startDate, endDate }],
    metrics,
    limit: 1,
    ...(i === 0 ? { returnPropertyQuota: true } : {}),
  }));

  return adapterOk({ requests: reports });
}

/**
 * Build batch 2: overview chunk 5 (overflow) + 4 breakdown reports.
 * 5 reports total (1 overview overflow + topPages + trafficSources + devices + geography).
 */
function buildBatch2Request(
  startDate: string,
  endDate: string
): AdapterResult<Record<string, unknown>> {
  const overflowChunk = OVERVIEW_METRICS_CHUNKS[5];
  const reports = [
    // Report 0: Overview overflow metrics (chunk 5)
    {
      dateRanges: [{ startDate, endDate }],
      metrics: overflowChunk!,
      limit: 1,
    },
    // Report 1: Top pages (dimension: pagePath, limit 10)
    {
      dateRanges: [{ startDate, endDate }],
      dimensions: [{ name: "pagePath" }],
      metrics: [{ name: "screenPageViews" }, { name: "activeUsers" }],
      orderBys: [
        { metric: { metricName: "screenPageViews" }, desc: true },
      ],
      limit: 10,
    },
    // Report 2: Traffic sources (dimension: sessionDefaultChannelGrouping, limit 10)
    {
      dateRanges: [{ startDate, endDate }],
      dimensions: [{ name: "sessionDefaultChannelGrouping" }],
      metrics: [{ name: "sessions" }, { name: "activeUsers" }],
      orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
      limit: 10,
    },
    // Report 3: Device categories (dimension: deviceCategory, limit 5)
    {
      dateRanges: [{ startDate, endDate }],
      dimensions: [{ name: "deviceCategory" }],
      metrics: [{ name: "sessions" }, { name: "activeUsers" }],
      orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
      limit: 5,
    },
    // Report 4: Geography (dimension: country, limit 10)
    {
      dateRanges: [{ startDate, endDate }],
      dimensions: [{ name: "country" }],
      metrics: [{ name: "sessions" }, { name: "activeUsers" }],
      orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
      limit: 10,
    },
  ];

  return adapterOk({ requests: reports });
}

/**
 * Build batch 3 request: demographic reports (newVsReturning, demographics, cities).
 * Sent as a separate batchRunReports call (5-report-per-request GA4 API limit).
 *
 * Note: Report index 1 (demographics) uses thresholded dimensions (userAgeBracket,
 * userGender) subject to 120 requests/hour quota when Google Signals is enabled.
 * When Signals is disabled, GA4 returns 200 OK with empty rows (NOT a 400 error).
 */
function buildBatch3Request(
  startDate: string,
  endDate: string
): AdapterResult<Record<string, unknown>> {
  const reports = [
    // Report 0: New vs Returning visitors
    {
      dateRanges: [{ startDate, endDate }],
      dimensions: [{ name: "newVsReturning" }],
      metrics: [
        { name: "sessions" },
        { name: "activeUsers" },
        { name: "averageSessionDuration" },
        { name: "screenPageViewsPerSession" },
      ],
      orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
      limit: 2,
    },
    // Report 1: Demographics (age bracket + gender) — requires Google Signals
    {
      dateRanges: [{ startDate, endDate }],
      dimensions: [{ name: "userAgeBracket" }, { name: "userGender" }],
      metrics: [{ name: "sessions" }, { name: "activeUsers" }],
      orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
      limit: 36,
    },
    // Report 2: Cities — top 10 by sessions
    {
      dateRanges: [{ startDate, endDate }],
      dimensions: [{ name: "city" }],
      metrics: [{ name: "sessions" }, { name: "activeUsers" }],
      orderBys: [{ metric: { metricName: "sessions" }, desc: true }],
      limit: 10,
    },
  ];

  return adapterOk({ requests: reports });
}

// ============================================================================
// Float Metric Detection (derived from GA4_METRICS — never manually maintained)
// ============================================================================

/**
 * Metrics that return float/decimal values from GA4 Data API.
 * Derived automatically from GA4_METRICS: any metric with type "percentage",
 * "currency", or "duration", or dataType "Decimal" or "Ratio" returns floats.
 * This prevents silent truncation when new float metrics are added to GA4_METRICS
 * but forgotten in a manual set.
 */
const GA4_FLOAT_METRIC_TYPES = new Set(["percentage", "currency", "duration"]);
const GA4_FLOAT_DATA_TYPES = new Set(["Decimal", "Ratio", "Currency", "Percentage", "Duration"]);

// Lazy-initialized on first use (after GA4_METRICS is defined below)
let _ga4FloatMetrics: Set<string> | null = null;
function getGA4FloatMetrics(): Set<string> {
  if (!_ga4FloatMetrics) {
    _ga4FloatMetrics = new Set(
      GA4_METRICS
        .filter((m) => GA4_FLOAT_METRIC_TYPES.has(m.type) || (m.dataType && GA4_FLOAT_DATA_TYPES.has(m.dataType)))
        .map((m) => m.key)
    );
  }
  return _ga4FloatMetrics;
}

// ============================================================================
// Response Transformation
// ============================================================================

/**
 * Transform a single GA4 report into flat records with _reportType discriminator.
 * All array access uses optional chaining (noUncheckedIndexedAccess compliance).
 */
function transformReport(
  report: GA4Report | undefined,
  reportType: ReportType
): Record<string, unknown>[] {
  if (!report) return [];

  const rows = report.rows ?? [];
  const metricIdx = buildHeaderIndex(report.metricHeaders);
  const dimIdx = buildHeaderIndex(report.dimensionHeaders);
  const records: Record<string, unknown>[] = [];

  for (const row of rows) {
    const record: Record<string, unknown> = { _reportType: reportType };

    // Add dimension values
    if (report.dimensionHeaders) {
      for (const [dimName, idx] of Object.entries(dimIdx)) {
        record[dimName] = row.dimensionValues?.[idx]?.value ?? "(unknown)";
      }
    }

    // Add metric values — determine int vs float based on metric name
    for (const [metricName, idx] of Object.entries(metricIdx)) {
      const rawValue = row.metricValues?.[idx]?.value;
      const isFloat = getGA4FloatMetrics().has(metricName);
      record[metricName] = parseMetricValue(rawValue, isFloat ? "float" : "int");
    }

    records.push(record);
  }

  return records;
}

/**
 * Transform batch 1 response (5 overview metric chunk reports) into a single
 * merged "overview" record. All 5 reports have no dimensions and limit 1,
 * so each produces exactly one row. We merge all metric values into one record.
 */
function transformBatch1Response(
  batchResponse: GA4BatchResponse | null
): Record<string, unknown>[] {
  if (!batchResponse) return [];
  const reports = batchResponse.reports ?? [];
  if (reports.length === 0) return [];

  // Merge all overview chunk reports into a single "overview" record
  const merged: Record<string, unknown> = { _reportType: "overview" as ReportType };
  for (let i = 0; i < BATCH1_OVERVIEW_COUNT; i++) {
    const report = reports[i];
    const records = transformReport(report, "overview");
    const row = records[0];
    if (row) {
      // Merge metric values (skip _reportType which is already set)
      for (const [key, value] of Object.entries(row)) {
        if (key !== "_reportType") merged[key] = value;
      }
    }
  }

  // Only emit the overview record if we got any data
  return Object.keys(merged).length > 1 ? [merged] : [];
}

/**
 * Transform batch 2 response: overview overflow (report 0) + breakdown reports (1-4).
 * Overview overflow metrics are merged into the existing overview record by the caller.
 */
function transformBatch2Response(
  batchResponse: GA4BatchResponse | null
): Record<string, unknown>[] {
  if (!batchResponse) return [];
  const reports = batchResponse.reports ?? [];
  const allRecords: Record<string, unknown>[] = [];

  for (const [indexStr, reportType] of Object.entries(REPORT_TYPE_MAP_BATCH2)) {
    const index = parseInt(indexStr, 10);
    const report = reports[index];
    const records = transformReport(report, reportType);
    allRecords.push(...records);
  }

  return allRecords;
}

/**
 * Transform batch 3 response (demographic reports) into AdapterMetrics records.
 * Handles Google Signals unavailability: when the demographics report returns
 * 200 OK with empty rows, we emit a record with `_available: false` so the
 * widget can render a "Google Signals required" message.
 */
function transformBatch3Response(
  batchResponse: GA4BatchResponse | null
): Record<string, unknown>[] {
  if (!batchResponse) return [];
  const reports = batchResponse.reports ?? [];
  const allRecords: Record<string, unknown>[] = [];

  for (const [indexStr, reportType] of Object.entries(REPORT_TYPE_MAP_BATCH3)) {
    const index = parseInt(indexStr, 10);
    const report = reports[index];

    // Handle Google Signals unavailability for demographics report
    if (reportType === "demographics" && report) {
      const rows = report.rows ?? [];
      if (rows.length === 0) {
        allRecords.push({
          _reportType: "demographics",
          _available: false,
          _unavailableReason: "google_signals_required",
        });
        continue;
      }
    }

    const records = transformReport(report, reportType);
    allRecords.push(...records);
  }

  return allRecords;
}

/**
 * Merge overview overflow records into the existing overview record.
 * Batch 2 report 0 is another overview chunk — its metrics must be
 * merged into the single overview record from batch 1.
 */
function mergeOverviewRecords(records: Record<string, unknown>[]): void {
  const overviewRecords = records.filter((r) => r._reportType === "overview");
  if (overviewRecords.length <= 1) return;

  // Merge all overflow overview records into the first one
  const primary = overviewRecords[0]!;
  for (let i = 1; i < overviewRecords.length; i++) {
    const overflow = overviewRecords[i]!;
    for (const [key, value] of Object.entries(overflow)) {
      if (key !== "_reportType") primary[key] = value;
    }
  }

  // Remove the overflow overview records, keep only the primary
  for (let i = records.length - 1; i >= 0; i--) {
    if (records[i]!._reportType === "overview" && records[i] !== primary) {
      records.splice(i, 1);
    }
  }
}

// ============================================================================
// Quota Logging
// ============================================================================

function logQuotaUsage(batchResponse: GA4BatchResponse | null): void {
  if (!batchResponse) return;
  // Quota info is on the first report of batch 1 (the one with returnPropertyQuota: true)
  const quota = batchResponse.reports?.[0]?.propertyQuota;
  if (!quota) return;

  const tokensPerDay = quota.tokensPerDay;
  if (tokensPerDay) {
    const total = tokensPerDay.consumed + tokensPerDay.remaining;
    const usedPct = total > 0 ? tokensPerDay.consumed / total : 0;

    console.log(
      JSON.stringify({
        event: "ga4_quota_usage",
        consumed: tokensPerDay.consumed,
        remaining: tokensPerDay.remaining,
        usedPct: Math.round(usedPct * 100) / 100,
      })
    );

    if (usedPct > 0.8) {
      console.log(
        JSON.stringify({
          event: "ga4_quota_warning",
          usedPct: Math.round(usedPct * 100) / 100,
          consumed: tokensPerDay.consumed,
          remaining: tokensPerDay.remaining,
        })
      );
    }
  }
}

// ============================================================================
// Available Metrics Definition
// ============================================================================

const GA4_METRICS: MetricDefinition[] = [
  // Core traffic (original)
  { key: "sessions", label: "Sessions", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "activeUsers", label: "Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "newUsers", label: "New Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "screenPageViews", label: "Pageviews", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "bounceRate", label: "Bounce Rate", type: "percentage", category: "engagement", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "sessions" } },
  { key: "averageSessionDuration", label: "Avg. Session Duration", type: "duration", category: "engagement", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "sessions" } },
  { key: "engagedSessions", label: "Engaged Sessions", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "engagementRate", label: "Engagement Rate", type: "percentage", category: "engagement", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "engagedSessions", denominator: "sessions" } },
  { key: "conversions", label: "Conversions", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "screenPageViewsPerSession", label: "Pages/Session", type: "number", category: "engagement", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "screenPageViews", denominator: "sessions" } },

  // Task 1: User/engagement metrics (AC #1)
  { key: "totalUsers", label: "Total Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "returningUsers", label: "Returning Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "sessionsPerUser", label: "Sessions/User", type: "number", category: "engagement", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "sessions", denominator: "totalUsers" } },
  { key: "userEngagementDuration", label: "User Engagement Duration", type: "duration", category: "engagement", dataType: "Duration", aggregationKind: "additive" },
  { key: "1dayActiveUsers", label: "1-Day Active Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "7dayActiveUsers", label: "7-Day Active Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "28dayActiveUsers", label: "28-Day Active Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "30dayActiveUsers", label: "30-Day Active Users", type: "number", category: "traffic", dataType: "Integer", aggregationKind: "additive" },
  { key: "dauPerMau", label: "DAU/MAU", type: "percentage", category: "engagement", dataType: "Ratio", aggregationKind: "ratio", ratioComponents: { numerator: "1dayActiveUsers", denominator: "30dayActiveUsers" } },
  { key: "dauPerWau", label: "DAU/WAU", type: "percentage", category: "engagement", dataType: "Ratio", aggregationKind: "ratio", ratioComponents: { numerator: "1dayActiveUsers", denominator: "7dayActiveUsers" } },
  { key: "wauPerMau", label: "WAU/MAU", type: "percentage", category: "engagement", dataType: "Ratio", aggregationKind: "ratio", ratioComponents: { numerator: "7dayActiveUsers", denominator: "30dayActiveUsers" } },

  // Task 2: E-commerce revenue metrics (AC #2)
  { key: "purchaseRevenue", label: "Purchase Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "totalRevenue", label: "Total Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "grossPurchaseRevenue", label: "Gross Purchase Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "refundAmount", label: "Refund Amount", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "revenuePerUser", label: "Revenue/User", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "totalRevenue", denominator: "totalUsers" } },
  { key: "totalAdRevenue", label: "Ad Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "shippingValue", label: "Shipping Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },

  // Task 3: E-commerce transaction/funnel metrics (AC #2)
  { key: "purchases", label: "Purchases", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "itemsPurchased", label: "Items Purchased", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "itemsViewed", label: "Items Viewed", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "addToCarts", label: "Add to Carts", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "checkouts", label: "Checkouts", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "firstTimePurchasers", label: "First-time Purchasers", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "purchaserConversionRate", label: "Purchaser Conversion Rate", type: "percentage", category: "conversions", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "totalPurchasers", denominator: "activeUsers" } },
  { key: "purchaseToViewRate", label: "Purchase-to-View Rate", type: "percentage", category: "conversions", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "itemsPurchased", denominator: "itemsViewed" } },
  { key: "totalPurchasers", label: "Total Purchasers", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "totalRefunds", label: "Total Refunds", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "cartViewers", label: "Cart Viewers", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "firstTimePurchasersConversionRate", label: "First-time Purchaser Rate", type: "percentage", category: "conversions", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "firstTimePurchasers", denominator: "activeUsers" } },

  // Task 4: Event metrics (AC #3)
  { key: "eventCount", label: "Event Count", type: "number", category: "events", dataType: "Integer", aggregationKind: "additive" },
  { key: "eventCountPerUser", label: "Events/User", type: "number", category: "events", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "eventCount", denominator: "totalUsers" } },
  { key: "eventsPerSession", label: "Events/Session", type: "number", category: "events", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "eventCount", denominator: "sessions" } },
  { key: "eventValue", label: "Event Value", type: "currency", category: "events", dataType: "Currency", aggregationKind: "additive" },
  { key: "keyEvents", label: "Key Events", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "userConversionRate", label: "User Conversion Rate", type: "percentage", category: "conversions", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "activeUsers" } },
  { key: "sessionKeyEventRate", label: "Session Key Event Rate", type: "percentage", category: "conversions", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "keyEvents", denominator: "sessions" } },

  // Task 5: Google Ads linked metrics (AC #4)
  { key: "advertiserAdClicks", label: "Ad Clicks (GA4)", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  { key: "advertiserAdImpressions", label: "Ad Impressions (GA4)", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  { key: "advertiserAdCost", label: "Ad Cost (GA4)", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "additive" },
  { key: "advertiserAdCostPerClick", label: "Ad CPC (GA4)", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "advertiserAdCost", denominator: "advertiserAdClicks" } },
  { key: "advertiserAdCostPerConversion", label: "Ad Cost/Conv (GA4)", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "advertiserAdCost", denominator: "conversions" } },

  // Task 6: Search Console linked metrics (AC #5)
  { key: "organicGoogleSearchClicks", label: "Organic Clicks (GSC)", type: "number", category: "seo", dataType: "Integer", aggregationKind: "additive" },
  { key: "organicGoogleSearchImpressions", label: "Organic Impressions (GSC)", type: "number", category: "seo", dataType: "Integer", aggregationKind: "additive" },
  { key: "organicGoogleSearchClickThroughRate", label: "Organic CTR (GSC)", type: "percentage", category: "seo", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "organicGoogleSearchClicks", denominator: "organicGoogleSearchImpressions" } },
  { key: "organicGoogleSearchAveragePosition", label: "Avg. Position (GSC)", type: "number", category: "seo", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "organicGoogleSearchImpressions" } },

  // Task 7: Scroll engagement (AC #7)
  { key: "scrolledUsers", label: "Scrolled Users", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
];

// ============================================================================
// Available Dimensions Definition (Story 5.34)
// ============================================================================

const GA4_DIMENSIONS: DimensionDefinition[] = [
  // Original dimensions
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01"] },
  { key: "dayOfWeek", label: "Day of Week", type: "Time", exampleValues: ["0", "1", "6"] },
  { key: "sessionSource", label: "Source", type: "Traffic", exampleValues: ["google", "direct", "facebook"] },
  { key: "sessionMedium", label: "Medium", type: "Traffic", exampleValues: ["organic", "cpc", "referral"] },
  { key: "sessionDefaultChannelGrouping", label: "Channel", type: "Traffic", exampleValues: ["Organic Search", "Paid Search", "Social"] },
  { key: "country", label: "Country", type: "Geo", exampleValues: ["US", "UK", "CA"] },
  { key: "city", label: "City", type: "Geo", exampleValues: ["New York", "London", "Toronto"] },
  { key: "userAgeBracket", label: "Age", type: "Demo", exampleValues: ["18-24", "25-34", "35-44"] },
  { key: "userGender", label: "Gender", type: "Demo", exampleValues: ["male", "female"] },
  { key: "deviceCategory", label: "Device", type: "Tech", exampleValues: ["desktop", "mobile", "tablet"] },
  { key: "browser", label: "Browser", type: "Tech", exampleValues: ["Chrome", "Safari", "Firefox"] },
  { key: "operatingSystem", label: "OS", type: "Tech", exampleValues: ["Windows", "macOS", "Android", "iOS"] },
  { key: "pagePath", label: "Page Path", type: "Content", exampleValues: ["/", "/about", "/pricing"] },
  { key: "pageTitle", label: "Page Title", type: "Content", exampleValues: ["Home", "About Us"] },
  { key: "landingPage", label: "Landing Page", type: "Content", exampleValues: ["/", "/blog/post-1"] },
  { key: "newVsReturning", label: "New vs Returning", type: "Audience", exampleValues: ["new", "returning"] },

  // Task 8: Expanded dimensions (AC #6)
  // Time
  { key: "hour", label: "Hour", type: "Time", exampleValues: ["0", "12", "23"] },
  { key: "month", label: "Month", type: "Time", exampleValues: ["01", "06", "12"] },
  { key: "isoWeek", label: "ISO Week", type: "Time", exampleValues: ["01", "26", "52"] },
  { key: "year", label: "Year", type: "Time", exampleValues: ["2025", "2026"] },
  // Traffic source (session-scoped)
  { key: "sessionSourceMedium", label: "Source / Medium", type: "Traffic", exampleValues: ["google / organic", "facebook / cpc"] },
  { key: "sessionCampaignName", label: "Campaign", type: "Traffic", exampleValues: ["spring_sale", "brand_awareness"] },
  { key: "sessionSourcePlatform", label: "Source Platform", type: "Traffic", exampleValues: ["Google Ads", "Manual"] },
  // Traffic source (first-user)
  { key: "firstUserSource", label: "First User Source", type: "Traffic", exampleValues: ["google", "facebook"] },
  { key: "firstUserMedium", label: "First User Medium", type: "Traffic", exampleValues: ["organic", "cpc"] },
  { key: "firstUserDefaultChannelGroup", label: "First User Channel", type: "Traffic", exampleValues: ["Organic Search", "Paid Social"] },
  { key: "firstUserCampaignName", label: "First User Campaign", type: "Traffic", exampleValues: ["spring_sale"] },
  // Geography
  { key: "region", label: "Region", type: "Geo", exampleValues: ["California", "Ontario", "England"] },
  { key: "continent", label: "Continent", type: "Geo", exampleValues: ["Americas", "Europe", "Asia"] },
  { key: "metro", label: "Metro", type: "Geo", exampleValues: ["New York NY", "Los Angeles CA"] },
  // Technology
  { key: "screenResolution", label: "Screen Resolution", type: "Tech", exampleValues: ["1920x1080", "1440x900"] },
  { key: "platform", label: "Platform", type: "Tech", exampleValues: ["web", "ios", "android"] },
  // Content
  { key: "hostName", label: "Hostname", type: "Content", exampleValues: ["example.com", "blog.example.com"] },
  { key: "contentGroup", label: "Content Group", type: "Content", exampleValues: ["blog", "product_pages"] },
  // Audience
  { key: "audienceName", label: "Audience", type: "Audience", exampleValues: ["All Users", "Purchasers"] },
  // Events
  { key: "eventName", label: "Event Name", type: "Conversion", exampleValues: ["page_view", "purchase", "scroll"] },
  { key: "isKeyEvent", label: "Is Key Event", type: "Conversion", exampleValues: ["true", "false"] },
  // E-commerce
  { key: "transactionId", label: "Transaction ID", type: "Product", exampleValues: ["TXN-001", "TXN-002"] },
  { key: "itemName", label: "Item Name", type: "Product", exampleValues: ["Blue T-Shirt", "Running Shoes"] },
  { key: "itemBrand", label: "Item Brand", type: "Product", exampleValues: ["Nike", "Adidas"] },
  { key: "itemCategory", label: "Item Category", type: "Product", exampleValues: ["Apparel", "Footwear"] },
  // Google Ads (session-scoped)
  { key: "sessionGoogleAdsCampaignName", label: "Google Ads Campaign", type: "Structure", exampleValues: ["Brand Campaign", "Shopping"] },
  { key: "sessionGoogleAdsAdGroupName", label: "Google Ads Ad Group", type: "Structure", exampleValues: ["Ad Group 1"] },
  { key: "sessionGoogleAdsKeyword", label: "Google Ads Keyword", type: "Search", exampleValues: ["running shoes", "buy coffee"] },
  { key: "sessionGoogleAdsAdNetworkType", label: "Ad Network Type", type: "Delivery", exampleValues: ["SEARCH", "DISPLAY", "YOUTUBE"] },
  // Search Console
  { key: "searchConsoleQuery", label: "Search Query (GSC)", type: "Search", exampleValues: ["best coffee beans", "running shoes"] },
  { key: "searchConsoleLandingPage", label: "Search Landing Page (GSC)", type: "Search", exampleValues: ["/blog/coffee-guide", "/products"] },
];

// ============================================================================
// GA4 Adapter (IntegrationAdapter implementation)
// ============================================================================

/**
 * GA4 adapter — plain object implementing IntegrationAdapter.
 * Typed explicitly as IntegrationAdapter (no `as` casts).
 *
 * NOTE: Token refresh is NOT implemented in this story (deferred to Story 4.12+).
 */
export const ga4Adapter: IntegrationAdapter = {
  platform: "google-analytics-4",

  rateLimits: {
    requestsPerMinute: 100,
    requestsPerDay: 50_000,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No GA4 property ID provided. Please select a property first.",
        retryable: false,
      });
    }

    // Convert Date objects to YYYY-MM-DD strings for GA4 API
    // Use the input Date objects directly — do NOT construct new Date objects from strings
    const startDateStr = dateRange.start.toISOString().split("T")[0];
    const endDateStr = dateRange.end.toISOString().split("T")[0];

    if (!startDateStr || !endDateStr) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid date range: could not format dates",
        retryable: false,
      });
    }

    // Build all 3 batch request bodies
    const batch1Result = buildBatch1Request(startDateStr, endDateStr);
    if (!batch1Result.success) {
      return adapterErr(batch1Result.error);
    }
    const batch2Result = buildBatch2Request(startDateStr, endDateStr);
    if (!batch2Result.success) {
      return adapterErr(batch2Result.error);
    }
    const batch3Result = buildBatch3Request(startDateStr, endDateStr);
    if (!batch3Result.success) {
      return adapterErr(batch3Result.error);
    }

    // Always prepend "properties/" to accountId for the Data API URL
    // The accountId stored in DB is numeric only (e.g., "987654321")
    const url = `${GA4_DATA_API_BASE}/${API_VERSIONS.google}/properties/${accountId}:batchRunReports`;
    const fetchOptions = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${credentials.accessToken}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    // Send all 3 batches concurrently
    // Batch 1: 5 overview metric chunks (50 metrics)
    // Batch 2: overview overflow (6 metrics) + 4 breakdown reports
    // Batch 3: 3 demographic reports
    const [result1, result2, result3] = await Promise.all([
      safeFetch(url, {
        ...fetchOptions,
        body: JSON.stringify(batch1Result.data),
      }),
      safeFetch(url, {
        ...fetchOptions,
        body: JSON.stringify(batch2Result.data),
      }),
      safeFetch(url, {
        ...fetchOptions,
        body: JSON.stringify(batch3Result.data),
      }),
    ]);

    // Batch 1 failure is fatal — core overview metrics are required
    if (!result1.success) {
      return adapterErr(result1.error);
    }

    // Consume response bodies EXACTLY ONCE (Failure 2c prevention)
    let batch1Response: GA4BatchResponse;
    try {
      batch1Response = (await result1.data.json()) as GA4BatchResponse;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse GA4 batch 1 response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    // Log quota usage (log-only, no stateful tracking — Failure 2a prevention)
    logQuotaUsage(batch1Response);

    // Transform batch 1 response (5 overview metric chunks → single merged overview record)
    const records = transformBatch1Response(batch1Response);

    // Batch 2 failure is non-fatal — breakdown data is supplementary.
    if (result2.success) {
      try {
        const batch2Response = (await result2.data.json()) as GA4BatchResponse;
        const batch2Records = transformBatch2Response(batch2Response);
        records.push(...batch2Records);
        // Merge overflow overview metrics into the primary overview record
        mergeOverviewRecords(records);
      } catch (err) {
        console.log(
          JSON.stringify({
            event: "ga4_batch2_parse_warning",
            message: `Failed to parse batch 2 response: ${err instanceof Error ? err.message : String(err)}`,
          })
        );
      }
    } else {
      console.log(
        JSON.stringify({
          event: "ga4_batch2_fetch_warning",
          code: result2.error.code,
          message: result2.error.message,
        })
      );
    }

    // Batch 3 failure is non-fatal — demographic data is supplementary.
    if (result3.success) {
      try {
        const batch3Response = (await result3.data.json()) as GA4BatchResponse;
        const batch3Records = transformBatch3Response(batch3Response);
        records.push(...batch3Records);
      } catch (err) {
        console.log(
          JSON.stringify({
            event: "ga4_batch3_parse_warning",
            message: `Failed to parse batch 3 response: ${err instanceof Error ? err.message : String(err)}`,
          })
        );
      }
    } else {
      console.log(
        JSON.stringify({
          event: "ga4_batch3_fetch_warning",
          code: result3.error.code,
          message: result3.error.message,
        })
      );
    }

    return adapterOk({
      platform: "google-analytics-4",
      accountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No GA4 property ID provided for connection validation.",
        retryable: false,
      });
    }

    // Lightweight call to getMetadata endpoint (5-second timeout)
    const url = `${GA4_DATA_API_BASE}/${API_VERSIONS.google}/properties/${accountId}/metadata`;

    const result = await safeFetch(
      url,
      {
        headers: {
          Authorization: `Bearer ${credentials.accessToken}`,
          Accept: "application/json",
        },
      },
      5_000
    );

    if (!result.success) {
      return adapterErr(result.error);
    }

    return adapterOk(true);
  },

  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Delegate to existing discoverGA4Properties() — DO NOT re-implement.
    // Story 31-15 AC#6: pass GRANTED scopes through (API-sourced — the OAuth
    // callback persists Google's token-response `scope` string), plus the
    // observability context for partial-enrichment warn logs. Bridge stays thin.
    return discoverGA4Properties(credentials.accessToken, {
      grantedScopes: credentials.scopes,
      logContext: { tenantId: ctx.tenantId, integrationId: ctx.integrationId },
    });
  },

  getAvailableMetrics(): MetricDefinition[] {
    return GA4_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return GA4_DIMENSIONS;
  },
};
